import type { YesNo } from "../../hooks/useLeadFormState";
import { RadioYesNo } from "./RadioYesNo";

type TradeInMode = "cash" | "agency" | null;

export default function TradeInStep({
  plateNumber,
  mileage,
  hasCtDefects,
  isFirstOwner,
  isImported,
  isMaintenanceUpToDate,
  tradeInMode,
  isAlreadyOnline,
  setPlateNumber,
  setMileage,
  setHasCtDefects,
  setIsFirstOwner,
  setIsImported,
  setIsMaintenanceUpToDate,
  setTradeInMode,
  setIsAlreadyOnline,
  onContinue,
  onBack,
}: {
  plateNumber: string;
  mileage: string;
  hasCtDefects: YesNo;
  isFirstOwner: YesNo;
  isImported: YesNo;
  isMaintenanceUpToDate: YesNo;
  tradeInMode: TradeInMode;
  isAlreadyOnline: YesNo;

  setPlateNumber: (v: string) => void;
  setMileage: (v: string) => void;
  setHasCtDefects: (v: YesNo) => void;
  setIsFirstOwner: (v: YesNo) => void;
  setIsImported: (v: YesNo) => void;
  setIsMaintenanceUpToDate: (v: YesNo) => void;
  setTradeInMode: (v: TradeInMode) => void;
  setIsAlreadyOnline: (v: YesNo) => void;

  onContinue: () => void;
  onBack?: () => void;
}) {
  const normalizedPlate = plateNumber
    .toUpperCase()
    .replace(/\s+/g, "")
    .replace(/[^A-Z0-9-]/g, "");

  const normalizedMileage = mileage.replace(/[^0-9]/g, "");

  const isValid =
    !!normalizedPlate.trim() &&
    !!normalizedMileage.trim() &&
    !!hasCtDefects &&
    !!isFirstOwner &&
    !!isImported &&
    !!isMaintenanceUpToDate &&
    !!tradeInMode &&
    !!isAlreadyOnline;

  return (
    <div>
      {onBack && (
        <button type="button" onClick={onBack} className="tdb-linkBtn">
          ← Retour
        </button>
      )}

      <div className="tdb-fields">
        <div className="tdb-field">
          <input
            name="plate"
            type="text"
            required
            placeholder="Plaque d'immatriculation *"
            value={plateNumber}
            onChange={(e) => setPlateNumber(e.target.value)}
            onBlur={() => {
              // normalize on blur (keeps typing flexible)
              if (plateNumber !== normalizedPlate)
                setPlateNumber(normalizedPlate);
            }}
            autoCapitalize="characters"
            className="tdb-input"
          />
        </div>

        <div className="tdb-field">
          <input
            name="mileage"
            type="text"
            inputMode="numeric"
            required
            placeholder="Kilomètres de votre véhicule *"
            value={mileage}
            onChange={(e) => setMileage(e.target.value)}
            onBlur={() => {
              if (mileage !== normalizedMileage) setMileage(normalizedMileage);
            }}
            className="tdb-input"
          />
        </div>

        <RadioYesNo
          name="tradein_ctDefects"
          value={hasCtDefects}
          onChange={setHasCtDefects}
          label="Avez-vous des défauts de contrôle technique ?"
        />

        <RadioYesNo
          name="tradein_firstOwner"
          value={isFirstOwner}
          onChange={setIsFirstOwner}
          label="Votre véhicule est-il une première main ?"
        />

        <RadioYesNo
          name="tradein_imported"
          value={isImported}
          onChange={setIsImported}
          label="Votre véhicule a-t-il été importé ?"
        />

        <RadioYesNo
          name="tradein_maintenance"
          value={isMaintenanceUpToDate}
          onChange={setIsMaintenanceUpToDate}
          label="L'entretien de votre véhicule est-il à jour ?"
        />

        <div className="tdb-field" style={{ alignItems: "flex-start" }}>
          <div style={{ width: "100%" }}>
            <div style={{ marginBottom: 10, fontWeight: 600, fontSize: 12 }}>
              Quel mode de reprise souhaitez-vous ?
            </div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <label
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  fontSize: 12,
                }}
              >
                <input
                  type="radio"
                  name="tradein_mode"
                  style={{ accentColor: "black", margin: 0 }}
                  checked={tradeInMode === "cash"}
                  onChange={() => setTradeInMode("cash")}
                />
                Reprise Cash
              </label>
              <label
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  fontSize: 12,
                }}
              >
                <input
                  type="radio"
                  name="tradein_mode"
                  style={{ accentColor: "black", margin: 0 }}
                  checked={tradeInMode === "agency"}
                  onChange={() => setTradeInMode("agency")}
                />
                Mandat de vente
              </label>
            </div>
          </div>
        </div>

        <RadioYesNo
          name="tradein_alreadyOnline"
          value={isAlreadyOnline}
          onChange={setIsAlreadyOnline}
          label="Votre véhicule est-il déjà en vente sur internet ?"
        />
      </div>

      <button
        type="button"
        onClick={() => {
          // Ensure values are normalized before continue
          if (plateNumber !== normalizedPlate) setPlateNumber(normalizedPlate);
          if (mileage !== normalizedMileage) setMileage(normalizedMileage);
          onContinue();
        }}
        disabled={!isValid}
        className="tdb-primaryBtn"
      >
        Continuer
      </button>
    </div>
  );
}
